// src/components/patient/Home/MapView.jsx
import React, { useEffect, useCallback, useRef, useState } from "react";

/* ---------- CSS Variables (Dark Mode Ready) ---------- */
const cssVars = `
  :root {
    --bg: #f9fafb;
    --card: rgba(255,255,255,0.98);
    --text: #111827;
    --text-light: #6b7280;
    --primary: #2563eb;
    --success: #10b981;
    --danger: #ef4444;
    --border: #e5e7eb;
    --shadow: 0 25px 50px rgba(0,0,0,0.12);
    --blur: blur(16px);
    --ring: #2563eb;
  }
  @media (prefers-color-scheme: dark) {
    :root {
      --bg: #0f172a;
      --card: rgba(30,41,59,0.98);
      --text: #f1f5f9;
      --text-light: #94a3b8;
      --primary: #3b82f6;
      --border: #334155;
      --shadow: 0 25px 50px rgba(0,0,0,0.3);
    }
  }
`;

/* ---------- Helpers ---------- */
// Palette
const COLORS = {
  blue: { chipBg: "#dbeafe", color: "#1d4ed8" },
  indigo: { chipBg: "#c7d2fe", color: "#6366f1" },
  sky: { chipBg: "#bfdbfe", color: "#2563eb" },
  teal: { chipBg: "#bbf7d0", color: "#0d9488" },
  green: { chipBg: "#bbf7d0", color: "#16a34a" },
  amber: { chipBg: "#fed7aa", color: "#d97706" },
  pink: { chipBg: "#fbcfe8", color: "#db2777" },
  slate: { chipBg: "#e0e7ff", color: "#4b5563" },
  violet: { chipBg: "#e9d5ff", color: "#9333ea" },
  red: { chipBg: "#fee2e2", color: "#dc2626" },
};

const SPECIALTY_MAP = [
  { tags: ["mjekësi e përgjithshme","mjekesi e pergjithshme","mjek i përgjithshëm","mjek i pergjithshem","general"], ...COLORS.slate, en: "General Medicine" },
  { tags: ["mjekësi familjare","mjekesi familjare","familjare","family"], ...COLORS.green, en: "Family Medicine" },
  { tags: ["mjekësi interne","mjekesi interne","interne","internal"], ...COLORS.slate, en: "Internal Medicine" },
  { tags: ["kardiologji","cardio","kardiolog"], ...COLORS.blue, en: "Cardiology" },
  { tags: ["kardiokirurgji","cardiac surgery"], ...COLORS.red, en: "Cardiothoracic Surgery" },
  { tags: ["pneumologji","pulmologji","pulmo","pneumo","respirator"], ...COLORS.sky, en: "Pulmonology" },
  { tags: ["gastroenterologji","gastro"], ...COLORS.amber, en: "Gastroenterology" },
  { tags: ["hepatologji","hepat"], ...COLORS.amber, en: "Hepatology" },
  { tags: ["endokrinologji","endokrin"], ...COLORS.violet, en: "Endocrinology" },
  { tags: ["diabetologji","diabet"], ...COLORS.violet, en: "Diabetology" },
  { tags: ["nefrologji","nefro"], ...COLORS.indigo, en: "Nephrology" },
  { tags: ["hematologji","hemato"], ...COLORS.red, en: "Hematology" },
  { tags: ["reumatologji","reuma"], ...COLORS.indigo, en: "Rheumatology" },
  { tags: ["sëmundje infektive","semundje infektive","infektive","infektologji"], ...COLORS.red, en: "Infectious Diseases" },
  { tags: ["alergologji","imunologji","alergologji dhe imunologji","allergology","immunology"], ...COLORS.teal, en: "Allergology & Immunology" },
  { tags: ["neurologji","neuro"], ...COLORS.indigo, en: "Neurology" },
  { tags: ["neurokirurgji","neurokirurg"], ...COLORS.indigo, en: "Neurosurgery" },
  { tags: ["dermatologji","derma","dermatolog"], ...COLORS.amber, en: "Dermatology" },
  { tags: ["oftalmologji","okulist","sy","ophthalmology"], ...COLORS.sky, en: "Ophthalmology" },
  { tags: ["orl","otorinolaringologji","otorinolaringolog","orl (otorinolaringologji)","orl)","(orl"], ...COLORS.teal, en: "ENT (Otolaryngology)" },
  { tags: ["gjinekologji","gjinekologji & obstetrikë","obstetrikë","obstetrike","obgyn","gjinekologji & obstetrike"], ...COLORS.pink, en: "Gynecology & Obstetrics" },
  { tags: ["urologji","urolog"], ...COLORS.sky, en: "Urology" },
  { tags: ["onkologji","onko"], ...COLORS.red, en: "Oncology" },
  { tags: ["radiologji"], ...COLORS.slate, en: "Radiology" },
  { tags: ["radiologji ndërhyrëse","radiologji nderhyrëse","nderhyrëse","ndërhyrëse","interventional radiology"], ...COLORS.slate, en: "Interventional Radiology" },
  { tags: ["anesteziologji","reanimacion","anesteziologji dhe reanimacion","anesthesia"], ...COLORS.slate, en: "Anesthesiology & ICU" },
  { tags: ["patologji","anatomo-patologji","anatomo patologji"], ...COLORS.slate, en: "Pathology (Anatomical)" },
  { tags: ["mjekësi e urgjencës","urgjencë","urgjence","emergency"], ...COLORS.red, en: "Emergency Medicine" },
  { tags: ["mjekësi e punës","mjekesi e punes","puna","work medicine","occupational"], ...COLORS.slate, en: "Occupational Medicine" },
  { tags: ["mjekësi sportive","sportive","sports medicine"], ...COLORS.green, en: "Sports Medicine" },
  { tags: ["mjekësi fizike dhe rehabilitim","rehabilitim","rehab","fizio mjekësor"], ...COLORS.green, en: "Physical Medicine & Rehabilitation" },
  { tags: ["ortopedi","ortoped"], ...COLORS.sky, en: "Orthopedics" },
  { tags: ["kirurgji","kirurg"], ...COLORS.red, en: "General Surgery" },
  { tags: ["kirurgji plastike","rikonstruktive","kirurgji plastike & rikonstruktive"], ...COLORS.pink, en: "Plastic & Reconstructive Surgery" },
  { tags: ["kirurgji torakale","torakale"], ...COLORS.sky, en: "Thoracic Surgery" },
  { tags: ["kirurgji vaskulare","vaskulare"], ...COLORS.sky, en: "Vascular Surgery" },
  { tags: ["kirurgji pediatrike","pediatrike kirurgji"], ...COLORS.green, en: "Pediatric Surgery" },
  { tags: ["pediatri","pediater","pediatër"], ...COLORS.green, en: "Pediatrics" },
  { tags: ["neonatologji","neonat"], ...COLORS.green, en: "Neonatology" },
  { tags: ["geriatri","geriatr"], ...COLORS.slate, en: "Geriatrics" },
  { tags: ["psikiatri","psikiater","psikiatër"], ...COLORS.violet, en: "Psychiatry" },
  { tags: ["psikiatri e fëmijëve","psikiatri e femijeve","adoleshentëve","adoleshenteve","child adolescent psychiatry"], ...COLORS.violet, en: "Child & Adolescent Psychiatry" },
  { tags: ["logopedi","logoped"], ...COLORS.indigo, en: "Speech Therapy (Logopedics)" },
  { tags: ["dietologji","nutricionizëm","nutricionizem","dietë","diete","nutrition"], ...COLORS.green, en: "Dietetics & Nutrition" },
  { tags: ["mjekësi laboratorike","laboratorike"], ...COLORS.slate, en: "Laboratory Medicine" },
  { tags: ["mikrobiologji","mikrobio"], ...COLORS.slate, en: "Microbiology" },
  { tags: ["infermier","infermiere","infermier/e"], ...COLORS.slate, en: "Nurse" },
  { tags: ["dentist","stomatolog","dentistri","dentistry"], ...COLORS.pink, en: "Dentist" },
  { tags: ["fizioterapist","fizioterapi","fizioterapist/e"], ...COLORS.green, en: "Physiotherapist" },
];

const normalize = (str = "") =>
  str
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim();

export const specialtyMeta = (name = "") => {
  const s = normalize(name);
  for (const item of SPECIALTY_MAP) {
    if (item.tags.some((t) => s.includes(normalize(t)))) {
      return { chipBg: item.chipBg, color: item.color, tag: item.en };
    }
  }
  if (!name) return { ...COLORS.slate, tag: "General" };
  return { ...COLORS.slate, tag: name };
};

const RouteIcon = () => (
  <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
  </svg>
);

/* --- Glossy Drop Pin --- */
const iconCache = new Map();

const buildHealthPin = (ring = "#2563eb", rating = null) => {
  const hasRating = rating != null && !isNaN(rating);
  const rounded = hasRating ? Number(rating).toFixed(1) : "";
  const key = `${ring}|${rounded}|32x40`;
  if (iconCache.has(key)) return iconCache.get(key);

  const svg = `
    <svg width="32" height="40" viewBox="0 0 32 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <filter id="shadow-${key}" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur in="SourceAlpha" stdDeviation="2"/>
          <feOffset dx="0" dy="2" result="offsetblur"/>
          <feComponentTransfer>
            <feFuncA type="linear" slope="0.3"/>
          </feComponentTransfer>
          <feMerge>
            <feMergeNode/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
        <linearGradient id="shine-${key}" x1="16" y1="0" x2="16" y2="32">
          <stop offset="0%" stop-color="#ffffff" stop-opacity="0.4"/>
          <stop offset="100%" stop-color="#ffffff" stop-opacity="0"/>
        </linearGradient>
      </defs>
      <!-- Pin shape -->
      <path d="M16 0C9.373 0 4 5.373 4 12c0 9 12 24 12 24s12-15 12-24c0-6.627-5.373-12-12-12z" 
            fill="${ring}" filter="url(#shadow-${key})"/>
      <!-- Inner circle -->
      <circle cx="16" cy="12" r="8" fill="white" opacity="0.95"/>
      <!-- Medical cross or star -->
      <path d="M16 7l1.545 3.13 3.455.5-2.5 2.435.59 3.435L16 15l-3.09 1.5.59-3.435-2.5-2.435 3.455-.5z" 
            fill="${ring}"/>
      <!-- Shine overlay -->
      <ellipse cx="16" cy="12" rx="10" ry="11" fill="url(#shine-${key})" opacity="0.6"/>
    </svg>
  `;

  const url = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
  iconCache.set(key, url);
  return url;
};

const makeDoctorPlaceholder = (name = "", ring = "#2563eb") => {
  const initials = (name || "Dr")
    .trim()
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase() || "DR";

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
    <svg width="80" height="80" viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg">
      <circle cx="40" cy="40" r="40" fill="${ring}"/>
      <text x="40" y="40" fill="white" font-family="system-ui,-apple-system,sans-serif" font-size="28" font-weight="700" text-anchor="middle" dominant-baseline="central">${initials}</text>
    </svg>
  `)}`;
};

export const HeartSolid = ({ size = 18, color = "#dc2626" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
    <path d="M11.645 20.91l-.007-.003-.022-.012a15.247 15.247 0 01-.383-.218 25.18 25.18 0 01-4.244-3.17C4.688 15.36 2.25 12.174 2.25 8.25 2.25 5.322 4.714 3 7.688 3A5.5 5.5 0 0112 5.052 5.5 5.5 0 0116.313 3c2.973 0 5.437 2.322 5.437 5.25 0 3.925-2.438 7.111-4.739 9.256a25.175 25.175 0 01-4.244 3.17 15.247 15.247 0 01-.383.219l-.022.012-.007.004-.003.001a.752.752 0 01-.704 0l-.003-.001z" />
  </svg>
);

export const HeartOutline = ({ size = 18, color = "#dc2626" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
  </svg>
);

// position helpers
const POS_KEY = (lat, lng) => `${(+lat).toFixed(6)}|${(+lng).toFixed(6)}`;
const JITTER_METERS = 25;
const toRad = (d) => (d * Math.PI) / 180;
const toDeg = (r) => (r * 180) / Math.PI;

const jitterPosition = (lat, lng, i, total) => {
  if (total === 1) return { lat, lng };
  const R = 6371000;
  const angle = (i * 2 * Math.PI) / total;
  const dist = JITTER_METERS * (1 + i * 0.25);
  const dy = dist * Math.cos(angle);
  const dx = (dist * Math.sin(angle)) / Math.cos(toRad(lat));
  return {
    lat: toDeg(toRad(lat) + dy / R),
    lng: toDeg(toRad(lng) + dx / R),
  };
};

const buildSameLocationGroups = (docs = []) => {
  const buckets = new Map();
  docs.forEach((d) => {
    if (!d?.position?.lat || !d?.position?.lng) return;
    const key = POS_KEY(d.position.lat, d.position.lng);
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(d);
  });

  const jittered = new Map();
  const groups = [];

  for (const [, arr] of buckets) {
    const base = arr[0].position;
    arr.forEach((doc, i) => {
      jittered.set(doc.id, jitterPosition(base.lat, base.lng, i, arr.length));
    });
    groups.push(arr);
  }

  return { jittered, groups };
};

/* ------------------------------ MapView ------------------------------ */
const MapView = ({
  isMapLoaded,
  doctors,
  userLocation,
  nearbyRadius,
  mapCenter,
  onDoctorClick,
  onToggleFollowDoctor,
  followedDoctors = {},
  followLoading = {},
  GOOGLE_MAPS_API_KEY,
  onBack,
}) => {
  const hostRef = useRef(null);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const userMarkerRef = useRef(null);
  const radiusCircleRef = useRef(null);
  const clustererRef = useRef(null);
  const resizeObsRef = useRef(null);
  const debouncedUpdateRef = useRef(null);

  const [selected, setSelected] = useState(null);
  const groupDataRef = useRef({ jittered: new Map(), groups: [] });
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupIndex, setGroupIndex] = useState(0);
  
  // Search and filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSpecialty, setSelectedSpecialty] = useState("all");
  const [showFilters, setShowFilters] = useState(false);
  const [radiusKm, setRadiusKm] = useState(nearbyRadius || 10);

  /* ---------- Filter doctors ---------- */
  const filteredDoctors = React.useMemo(() => {
    let filtered = doctors || [];
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = normalize(searchQuery);
      filtered = filtered.filter((d) => {
        const name = normalize(d.name || "");
        const specialty = normalize(d.specialty || "");
        const address = normalize(d.location?.address || "");
        return name.includes(query) || specialty.includes(query) || address.includes(query);
      });
    }
    
    // Apply specialty filter
    if (selectedSpecialty !== "all") {
      filtered = filtered.filter((d) => {
        const meta = specialtyMeta(d.specialty);
        return meta.tag === selectedSpecialty;
      });
    }
    
    // Apply distance filter
    if (userLocation) {
      filtered = filtered.filter((d) => !d.distance || d.distance <= radiusKm);
    }
    
    return filtered;
  }, [doctors, searchQuery, selectedSpecialty, userLocation, radiusKm]);

  // Get unique specialties from doctors
  const specialties = React.useMemo(() => {
    const specs = new Set();
    (doctors || []).forEach((d) => {
      const meta = specialtyMeta(d.specialty);
      specs.add(meta.tag);
    });
    return Array.from(specs).sort();
  }, [doctors]);

  /* ---------- map init & markers ---------- */
  const clearMarkers = useCallback(() => {
    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];
    clustererRef.current?.clearMarkers();
    clustererRef.current = null;
    userMarkerRef.current?.setMap(null);
    userMarkerRef.current = null;
    radiusCircleRef.current?.setMap(null);
    radiusCircleRef.current = null;
  }, []);

  const initializeMap = useCallback(() => {
    if (!mapRef.current || !window.google || mapInstanceRef.current) return;

    const map = new window.google.maps.Map(mapRef.current, {
      center: mapCenter,
      zoom: userLocation ? 13 : 12,
      styles: [
        { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
        { featureType: "transit", elementType: "labels", stylers: [{ visibility: "off" }] },
      ],
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      zoomControl: true,
      gestureHandling: "greedy",
    });

    map.addListener("click", () => {
      setSelected(null);
      setSelectedGroup(null);
    });

    mapInstanceRef.current = map;

    if (userLocation) {
      userMarkerRef.current = new window.google.maps.Marker({
        position: userLocation,
        map,
        icon: {
          url: "https://maps.gstatic.com/mapfiles/ms2/micons/blue-dot.png",
        },
        zIndex: 1000,
        title: "Vendndodhja juaj",
      });

      if (radiusKm > 0) {
        radiusCircleRef.current = new window.google.maps.Circle({
          strokeColor: "#3b82f6",
          strokeOpacity: 0.4,
          strokeWeight: 2,
          fillColor: "#3b82f6",
          fillOpacity: 0.1,
          map,
          center: userLocation,
          radius: radiusKm * 1000,
        });
      }
    }
  }, [mapCenter, userLocation, radiusKm]);

  // Update radius circle when radiusKm changes
  useEffect(() => {
    if (radiusCircleRef.current && userLocation) {
      radiusCircleRef.current.setRadius(radiusKm * 1000);
    }
  }, [radiusKm, userLocation]);

  const ensureClustererLoaded = () =>
    new Promise((resolve) => {
      if (window.markerClusterer) return resolve(true);
      const s = document.createElement("script");
      s.src = "https://unpkg.com/@googlemaps/markerclusterer/dist/index.min.js";
      s.onload = () => resolve(true);
      s.onerror = () => resolve(false);
      document.head.appendChild(s);
    });

  const updateMarkers = useCallback(async () => {
    if (!mapInstanceRef.current) return;
    clearMarkers();

    const visible = filteredDoctors;

    const { jittered, groups } = buildSameLocationGroups(visible);
    groupDataRef.current = { jittered, groups };

    const bounds = new window.google.maps.LatLngBounds();

    visible.forEach((doctor) => {
      if (!doctor.position?.lat || !doctor.position?.lng) return;

      const rating = doctor.rating ?? doctor.reviewsAvg ?? null;
      const pin = buildHealthPin("#2563eb", rating);
      const pos = jittered.get(doctor.id) || doctor.position;

      const marker = new window.google.maps.Marker({
        position: pos,
        map: null,
        title: `${doctor.name}`,
        icon: {
          url: pin,
          scaledSize: new window.google.maps.Size(32, 40),
          anchor: new window.google.maps.Point(16, 40),
        },
      });

      marker.addListener("click", () => {
        const group = groupDataRef.current.groups.find((g) => g.some((x) => x.id === doctor.id)) || [doctor];
        setSelectedGroup(group);
        const idx = group.findIndex((x) => x.id === doctor.id);
        setGroupIndex(idx >= 0 ? idx : 0);
        setSelected(doctor);
        mapInstanceRef.current?.panTo(pos);
        mapInstanceRef.current?.panBy(0, -160);
      });

      marker.addListener("mouseover", () =>
        marker.setIcon({
          url: buildHealthPin("#2563eb", rating),
          scaledSize: new window.google.maps.Size(40, 50),
          anchor: new window.google.maps.Point(20, 50),
        })
      );

      marker.addListener("mouseout", () =>
        marker.setIcon({
          url: pin,
          scaledSize: new window.google.maps.Size(32, 40),
          anchor: new window.google.maps.Point(16, 40),
        })
      );

      markersRef.current.push(marker);
      bounds.extend(pos);
    });

    if (userLocation) bounds.extend(userLocation);

    const ok = await ensureClustererLoaded();
    if (ok && window.markerClusterer) {
      clustererRef.current = new window.markerClusterer.MarkerClusterer({
        map: mapInstanceRef.current,
        markers: markersRef.current,
        renderer: {
          render: ({ count, position }) =>
            new window.google.maps.Marker({
              position,
              icon: {
                url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
                  <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                      <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
                        <feGaussianBlur in="SourceAlpha" stdDeviation="1.5"/>
                        <feOffset dx="0" dy="1" result="offsetblur"/>
                        <feComponentTransfer>
                          <feFuncA type="linear" slope="0.3"/>
                        </feComponentTransfer>
                        <feMerge>
                          <feMergeNode/>
                          <feMergeNode in="SourceGraphic"/>
                        </feMerge>
                      </filter>
                    </defs>
                    <circle cx="20" cy="20" r="18" fill="#2563eb" stroke="white" stroke-width="2.5" filter="url(#shadow)"/>
                    <text x="20" y="20" fill="white" font-family="system-ui,-apple-system,sans-serif" font-size="13" font-weight="700" text-anchor="middle" dominant-baseline="central">${count}</text>
                  </svg>
                `)}`,
                scaledSize: new window.google.maps.Size(40, 40),
                anchor: new window.google.maps.Point(20, 20),
              },
            }),
        },
      });
    } else {
      markersRef.current.forEach((m) => m.setMap(mapInstanceRef.current));
    }

    if (visible.length && userLocation) {
      mapInstanceRef.current.fitBounds(bounds);
      const listener = window.google.maps.event.addListener(mapInstanceRef.current, "idle", () => {
        if (mapInstanceRef.current.getZoom() > 16) {
          mapInstanceRef.current.setZoom(16);
        }
        window.google.maps.event.removeListener(listener);
      });
    }
  }, [clearMarkers, filteredDoctors, userLocation]);

  /* ---------- effects ---------- */
  useEffect(() => {
    if (isMapLoaded) initializeMap();
  }, [isMapLoaded, initializeMap]);

  useEffect(() => {
    if (!mapRef.current || !mapInstanceRef.current) return;

    if (resizeObsRef.current) resizeObsRef.current.disconnect();
    resizeObsRef.current = new ResizeObserver(() => {
      window.google?.maps.event.trigger(mapInstanceRef.current, "resize");
    });
    resizeObsRef.current.observe(mapRef.current);

    return () => resizeObsRef.current?.disconnect();
  }, []);

  useEffect(() => {
    clearTimeout(debouncedUpdateRef.current);
    debouncedUpdateRef.current = setTimeout(updateMarkers, 80);
    return () => clearTimeout(debouncedUpdateRef.current);
  }, [updateMarkers, filteredDoctors]);

  useEffect(() => () => clearMarkers(), [clearMarkers]);

  /* ---------- loading state ---------- */
  if (!isMapLoaded) {
    return (
      <div
        ref={hostRef}
        style={{
          width: "100%",
          height: "100%",
          minHeight: 480,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "var(--bg)",
          borderRadius: 24,
          border: "1px solid var(--border)",
          paddingBottom: "80px",
    boxSizing: "border-box",
        }}
      >
        <div style={{ textAlign: "center", color: "var(--text-light)" }}>
          {GOOGLE_MAPS_API_KEY ? "Po ngarkohet harta..." : "Mungon REACT_APP_GOOGLE_MAPS_API_KEY"}
        </div>
      </div>
    );
  }

  /* ---------- bottom sheet card ---------- */
  const Card = () => {
    const doc = selectedGroup?.length ? selectedGroup[groupIndex] : selected;
    if (!doc) return null;

    const d = doc;
    const meta = specialtyMeta(d.specialty);
    const photo = d.photoUrl || makeDoctorPlaceholder(d.name);
    const rating = d.rating ?? d.reviewsAvg ?? 5.0;
    const reviews = d.reviewsCount ?? 0;
    const isFollowing = !!followedDoctors[d.id];
    const activeDocId = d.id;

    return (
     <div
  onClick={(e) => e.stopPropagation()}
  style={{
    position: "absolute",
    left: 0,
    right: 0,
    // 🚨 key change: lift the sheet above browser navbar
    bottom: 80, // try 80, you can tweak to 70 / 90 if needed

    zIndex: 1200,
    background: "var(--card)",
    backdropFilter: "var(--blur)",
    borderTopLeftRadius: 28,
    borderTopRightRadius: 28,
    boxShadow: "0 -10px 40px rgba(0,0,0,0.15)",
    padding: "20px 20px 32px 20px",

    // optional: make sure the card scrolls if too tall
    maxHeight: "calc(100vh - 140px)",
    overflowY: "auto",

    animation: "slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
  }}
>


        <style>{`
          @keyframes slideUp {
            from { transform: translateY(100%); opacity: 0; }
            to { transform: translateY(0); opacity: 1; }
          }
          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.1); }
          }
        `}</style>

        <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
          <img
            src={photo}
            alt={d.name}
            style={{
              width: 80,
              height: 80,
              borderRadius: 16,
              objectFit: "cover",
              border: "2px solid var(--border)",
              background: "var(--bg)",
              flexShrink: 0,
            }}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                display: "inline-block",
                background: meta.chipBg,
                color: meta.color,
                padding: "4px 10px",
                borderRadius: 8,
                fontSize: 11,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                marginBottom: 8,
              }}
            >
              {meta.tag}
            </div>
            <h3
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 800,
                color: "var(--text)",
                marginBottom: 4,
                lineHeight: 1.3,
              }}
            >
              {d.name}
            </h3>
            <p
              style={{
                margin: 0,
                fontSize: 13,
                color: "var(--text-light)",
                display: "flex",
                alignItems: "center",
                gap: 4,
                marginBottom: 8,
              }}
            >
              <svg width="14" height="14" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
              </svg>
              {d.location?.address || "Vendndodhja e disponueshme"}
            </p>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontSize: 13,
                color: "var(--text)",
              }}
            >
              <span style={{ display: "flex", alignItems: "center", gap: 4, fontWeight: 600 }}>
                ★ {Number(rating).toFixed(1)}
                <span style={{ fontWeight: 400, color: "var(--text-light)" }}>({reviews})</span>
              </span>
              {d.distanceText && (
                <>
                  <span style={{ color: "var(--border)" }}>•</span>
                  <span style={{ color: "var(--text-light)" }}>{d.distanceText}</span>
                </>
              )}
            </div>
          </div>

          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleFollowDoctor?.(activeDocId);
            }}
            disabled={!!followLoading[activeDocId]}
            style={{
              alignSelf: "flex-start",
              background: isFollowing ? "#fee2e2" : "transparent",
              color: isFollowing ? "#dc2626" : "var(--text-light)",
              border: `2px solid ${isFollowing ? "#fecaca" : "var(--border)"}`,
              borderRadius: 12,
              width: 44,
              height: 44,
              fontSize: 20,
              display: "grid",
              placeItems: "center",
              cursor: "pointer",
              transition: "all 0.2s",
              animation: isFollowing ? "pulse 0.6s" : "none",
              flexShrink: 0,
            }}
            aria-label={isFollowing ? "Hiq nga të ndjekurit" : "Ndiq"}
          >
            {isFollowing ? <HeartSolid size={20} /> : <HeartOutline size={20} />}
          </button>
        </div>

        {selectedGroup?.length > 1 && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 12,
              padding: "12px 0",
              marginBottom: 12,
              borderTop: "1px solid var(--border)",
              borderBottom: "1px solid var(--border)",
            }}
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                setGroupIndex((i) => (i - 1 + selectedGroup.length) % selectedGroup.length);
              }}
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                border: "1px solid var(--border)",
                background: "var(--card)",
                cursor: "pointer",
                display: "grid",
                placeItems: "center",
                fontSize: 16,
                color: "var(--text)",
                fontWeight: 700,
              }}
              aria-label="Mjeku i mëparshëm"
            >
              ←
            </button>
            <span style={{ fontSize: 14, fontWeight: 600, color: "var(--text)" }}>
              {groupIndex + 1} / {selectedGroup.length}
            </span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setGroupIndex((i) => (i + 1) % selectedGroup.length);
              }}
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                border: "1px solid var(--border)",
                background: "var(--card)",
                cursor: "pointer",
                display: "grid",
                placeItems: "center",
                fontSize: 16,
                color: "var(--text)",
                fontWeight: 700,
              }}
              aria-label="Mjeku tjetër"
            >
              →
            </button>
          </div>
        )}

        <div style={{ display: "flex", gap: 12 }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDoctorClick?.(activeDocId);
            }}
            style={{
              flex: 1,
              background: "linear-gradient(135deg, #10b981, #059669)",
              color: "#fff",
              border: "none",
              borderRadius: 14,
              padding: "16px",
              fontWeight: 700,
              fontSize: 15,
              cursor: "pointer",
              boxShadow: "0 4px 14px rgba(16,185,129,0.35)",
              transition: "all 0.2s",
            }}
            onMouseOver={(e) => (e.currentTarget.style.transform = "translateY(-2px)")}
            onMouseOut={(e) => (e.currentTarget.style.transform = "translateY(0)")}
          >
            Shiko profilin
          </button>

          {d.position && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                window.open(
                  `https://www.google.com/maps/dir/?api=1&destination=${d.position.lat},${d.position.lng}`,
                  "_blank"
                );
              }}
              style={{
                width: 56,
                height: 56,
                borderRadius: 14,
                border: "1px solid var(--border)",
                background: "var(--card)",
                cursor: "pointer",
                display: "grid",
                placeItems: "center",
                color: "var(--primary)",
                transition: "all 0.2s",
              }}
              aria-label="Udhëzimet"
              onMouseOver={(e) => (e.currentTarget.style.background = "var(--primary)", e.currentTarget.style.color = "#fff")}
              onMouseOut={(e) => (e.currentTarget.style.background = "var(--card)", e.currentTarget.style.color = "var(--primary)")}
            >
              <RouteIcon />
            </button>
          )}

          {d.phone && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                window.location.href = `tel:${d.phone}`;
              }}
              style={{
                width: 56,
                height: 56,
                borderRadius: 14,
                border: "1px solid var(--border)",
                background: "var(--card)",
                cursor: "pointer",
                display: "grid",
                placeItems: "center",
                color: "var(--success)",
                transition: "all 0.2s",
              }}
              aria-label="Telefono"
              onMouseOver={(e) => (e.currentTarget.style.background = "var(--success)", e.currentTarget.style.color = "#fff")}
              onMouseOut={(e) => (e.currentTarget.style.background = "var(--card)", e.currentTarget.style.color = "var(--success)")}
            >
              <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
              </svg>
            </button>
          )}
        </div>

        <button
          onClick={(e) => {
            e.stopPropagation();
            setSelected(null);
            setSelectedGroup(null);
          }}
          style={{
            width: 48,
            height: 5,
            background: "var(--border)",
            borderRadius: 999,
            cursor: "pointer",
            border: "none",
            position: "absolute",
            top: 12,
            left: "50%",
            transform: "translateX(-50%)",
          }}
          aria-label="Mbyll"
        />
      </div>
    );
  };

  /* ---------- render ---------- */
  return (
    <>
      <style>{cssVars}</style>
      <div
        ref={hostRef}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          zIndex: 9999,
          overflow: "hidden",
        }}
      >
        <div ref={mapRef} style={{ width: "100%", height: "100%", borderRadius: "inherit" }} />

        {/* Header with Back Button, Search, and Filters */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1100,
            padding: "16px",
            background: "linear-gradient(180deg, rgba(0,0,0,0.3) 0%, transparent 100%)",
          }}
        >
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            {/* Back Button */}
            <button
              onClick={onBack}
              style={{
                width: 48,
                height: 48,
                borderRadius: 14,
                background: "var(--card)",
                backdropFilter: "var(--blur)",
                border: "1px solid var(--border)",
                boxShadow: "0 8px 20px rgba(0,0,0,0.12)",
                cursor: "pointer",
                display: "grid",
                placeItems: "center",
                color: "var(--text)",
                transition: "all 0.2s",
                flexShrink: 0,
              }}
              aria-label="Kthehu mbrapa"
              onMouseOver={(e) => (e.currentTarget.style.transform = "scale(1.05)")}
              onMouseOut={(e) => (e.currentTarget.style.transform = "scale(1)")}
            >
              <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            {/* Search Bar */}
            <div
              style={{
                flex: 1,
                position: "relative",
                display: "flex",
                alignItems: "center",
              }}
            >
              <svg
                width="20"
                height="20"
                fill="none"
                stroke="var(--text-light)"
                strokeWidth={2}
                viewBox="0 0 24 24"
                style={{
                  position: "absolute",
                  left: 16,
                  pointerEvents: "none",
                }}
              >
                <circle cx="11" cy="11" r="8" />
                <path strokeLinecap="round" d="M21 21l-4.35-4.35" />
              </svg>
              <input
                type="text"
                placeholder="Kërko mjekë, specialitet, vendndodhje..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: "100%",
                  height: 48,
                  borderRadius: 14,
                  background: "var(--card)",
                  backdropFilter: "var(--blur)",
                  border: "1px solid var(--border)",
                  boxShadow: "0 8px 20px rgba(0,0,0,0.12)",
                  paddingLeft: 48,
                  paddingRight: 16,
                  fontSize: 15,
                  color: "var(--text)",
                  outline: "none",
                }}
                onFocus={(e) => (e.currentTarget.style.borderColor = "var(--primary)")}
                onBlur={(e) => (e.currentTarget.style.borderColor = "var(--border)")}
              />
            </div>

            {/* Filter Button */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              style={{
                width: 48,
                height: 48,
                borderRadius: 14,
                background: showFilters ? "var(--primary)" : "var(--card)",
                backdropFilter: "var(--blur)",
                border: `1px solid ${showFilters ? "var(--primary)" : "var(--border)"}`,
                boxShadow: "0 8px 20px rgba(0,0,0,0.12)",
                cursor: "pointer",
                display: "grid",
                placeItems: "center",
                color: showFilters ? "#fff" : "var(--text)",
                transition: "all 0.2s",
                flexShrink: 0,
                position: "relative",
              }}
              aria-label="Filtro"
              onMouseOver={(e) => (e.currentTarget.style.transform = "scale(1.05)")}
              onMouseOut={(e) => (e.currentTarget.style.transform = "scale(1)")}
            >
              <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 01-.659 1.591l-5.432 5.432a2.25 2.25 0 00-.659 1.591v2.927a2.25 2.25 0 01-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 00-.659-1.591L3.659 7.409A2.25 2.25 0 013 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0112 3z" />
              </svg>
              {(selectedSpecialty !== "all" || (userLocation && radiusKm !== nearbyRadius)) && (
                <span
                  style={{
                    position: "absolute",
                    top: -4,
                    right: -4,
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                    background: "#ef4444",
                    color: "#fff",
                    fontSize: 11,
                    fontWeight: 700,
                    display: "grid",
                    placeItems: "center",
                  }}
                >
                  {(selectedSpecialty !== "all" ? 1 : 0) + (userLocation && radiusKm !== nearbyRadius ? 1 : 0)}
                </span>
              )}
            </button>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <div
              style={{
                marginTop: 12,
                background: "var(--card)",
                backdropFilter: "var(--blur)",
                border: "1px solid var(--border)",
                borderRadius: 16,
                padding: 16,
                boxShadow: "0 8px 20px rgba(0,0,0,0.12)",
                animation: "slideDown 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
              }}
            >
              <style>{`
                @keyframes slideDown {
                  from { opacity: 0; transform: translateY(-10px); }
                  to { opacity: 1; transform: translateY(0); }
                }
                input[type="range"] {
                  -webkit-appearance: none;
                  width: 100%;
                  height: 6px;
                  border-radius: 3px;
                  background: var(--border);
                  outline: none;
                }
                input[type="range"]::-webkit-slider-thumb {
                  -webkit-appearance: none;
                  appearance: none;
                  width: 20px;
                  height: 20px;
                  border-radius: 50%;
                  background: var(--primary);
                  cursor: pointer;
                  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
                }
                input[type="range"]::-moz-range-thumb {
                  width: 20px;
                  height: 20px;
                  border-radius: 50%;
                  background: var(--primary);
                  cursor: pointer;
                  border: none;
                  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
                }
              `}</style>

              {/* User Location Display */}
              {userLocation && (
                <div
                  style={{
                    background: "linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)",
                    padding: "12px 14px",
                    borderRadius: 12,
                    marginBottom: 16,
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                  }}
                >
                  <svg width="20" height="20" fill="#2563eb" viewBox="0 0 24 24">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                  </svg>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#1e40af", marginBottom: 2 }}>
                      VENDNDODHJA JUAJ
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#2563eb" }}>
                      {userLocation.lat.toFixed(6)}, {userLocation.lng.toFixed(6)}
                    </div>
                  </div>
                </div>
              )}

              {/* Distance Radius Slider */}
              {userLocation && (
                <div style={{ marginBottom: 16 }}>
                  <label
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      fontSize: 13,
                      fontWeight: 700,
                      color: "var(--text)",
                      marginBottom: 8,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    <span>Rrezja e kërkimit</span>
                    <span style={{ color: "var(--primary)", fontSize: 15 }}>{radiusKm} km</span>
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="50"
                    step="1"
                    value={radiusKm}
                    onChange={(e) => setRadiusKm(Number(e.target.value))}
                    style={{
                      width: "100%",
                    }}
                  />
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: 11,
                      color: "var(--text-light)",
                      marginTop: 4,
                    }}
                  >
                    <span>1 km</span>
                    <span>50 km</span>
                  </div>
                </div>
              )}

              {/* Specialty Filter */}
              <div style={{ marginBottom: 12 }}>
                <label
                  style={{
                    display: "block",
                    fontSize: 13,
                    fontWeight: 700,
                    color: "var(--text)",
                    marginBottom: 8,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Specialiteti
                </label>
                <select
                  value={selectedSpecialty}
                  onChange={(e) => setSelectedSpecialty(e.target.value)}
                  style={{
                    width: "100%",
                    height: 44,
                    borderRadius: 10,
                    background: "var(--bg)",
                    border: "1px solid var(--border)",
                    padding: "0 12px",
                    fontSize: 14,
                    color: "var(--text)",
                    cursor: "pointer",
                    outline: "none",
                  }}
                  onFocus={(e) => (e.currentTarget.style.borderColor = "var(--primary)")}
                  onBlur={(e) => (e.currentTarget.style.borderColor = "var(--border)")}
                >
                  <option value="all">Të gjithë specialitetët</option>
                  {specialties.map((spec) => (
                    <option key={spec} value={spec}>
                      {spec}
                    </option>
                  ))}
                </select>
              </div>
              
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button
                  onClick={() => {
                    setSearchQuery("");
                    setSelectedSpecialty("all");
                    setRadiusKm(nearbyRadius || 10);
                  }}
                  style={{
                    padding: "8px 16px",
                    borderRadius: 8,
                    background: "transparent",
                    border: "1px solid var(--border)",
                    color: "var(--text)",
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Pastro
                </button>
                <button
                  onClick={() => setShowFilters(false)}
                  style={{
                    padding: "8px 16px",
                    borderRadius: 8,
                    background: "var(--primary)",
                    border: "none",
                    color: "#fff",
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Apliko
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Results Counter */}
        <div
          style={{
            position: "absolute",
            bottom: `calc(${selected ? 260 : 24}px + env(safe-area-inset-bottom, 0px))`,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1100,
            background: "var(--card)",
            backdropFilter: "var(--blur)",
            border: "1px solid var(--border)",
            borderRadius: 12,
            padding: "8px 16px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            fontSize: 13,
            fontWeight: 600,
            color: "var(--text)",
            transition: "all 0.3s",
          }}
        >
          {filteredDoctors.length} {filteredDoctors.length === 1 ? "mjek" : "mjekë"} {searchQuery || selectedSpecialty !== "all" ? "u gjetën" : "të disponueshëm"}
        </div>

        {selected && <Card />}
      </div>
    </>
  );
};

export default MapView;